import Backbone, {Model} from 'backbone';
import $ from 'jquery';
import React from 'react';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import * as alignmentviewutil from 'analyses/alignment/AlignmentViewUtil';
import PolymerAlignmentRowView, {
  PolymerAlignmentRowViewInitializationOptions,
} from 'analyses/alignment/PolymerAlignmentRowView';
import {MinimalAlignmentRowsView} from 'analyses/alignment/PolymerAlignmentTabView';
import {AlignmentRowProps} from 'analyses/AlignmentRow';
import {Alignment} from 'analyses/Analyses';
import {assertAndContinue} from 'Asserts';
import {GAP} from 'biojs/BioConstants';
import * as util from 'biojs/Util';
import {AlignmentRowVisibilityOptionsDropdown} from 'components/DisplayOptions';
import {MenuChoicesOptions} from 'components/SelectionContextMenuHelpers';
import * as vo from 'components/VisibilityOptions';
import {DNAAlignmentKind, ProteinAlignmentKind} from 'constants/AlignmentConstants';
import * as MapsConstants from 'constants/MapsConstants';
import staticURL from 'lib/staticURL';
import AlignmentSelectionProps from 'molbio/alignments/backbone/AlignmentSelectionProps';
import * as infinitescroller from 'molbio/maps/linear/InfiniteScroller';
import {
  InfiniteScrollbar,
  InfiniteScroller,
  MinimalSliceView,
  ScrollData,
} from 'molbio/maps/linear/InfiniteScroller';
import Slice from 'molbio/maps/linear/Slice';
import {SliceView} from 'molbio/maps/linear/SliceView';
import TraceView from 'molbio/maps/linear/TraceView';
import {AlignmentMapState} from 'molbio/maps/MapStates';
import Range from 'molbio/maps/selectables/Range';
import {Selection} from 'molbio/maps/selectables/Selection';
import {foreignObjectRenderingEnabled} from 'molbio/maps/SequenceMapConstants';
import SequenceMapUtil, {ShowInputBubbleOptions} from 'molbio/maps/SequenceMapUtil';
import AlignmentTabState from 'molbio/tabstates/AlignmentTabState';
import * as popup from 'Popup';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import addTestAttrsToDOMElement from 'test/lib/cypress/addTestAttrsToDOMElement';
import setTestAttrs from 'test/lib/cypress/setTestAttrs';
import {verticallyScrollItemIntoView} from 'util/Scroll';

import AlignmentRowInfo from './AlignmentRowInfo';
import SequenceAlignmentTabView from './SequenceAlignmentTabView';

const {ALIGNMENT_TYPE} = MapsConstants;

interface RowsView extends MinimalAlignmentRowsView {
  handleAlignmentSort(): void;
  jumpToIndex(index: number): void;
}

/**
 * Debounce the slider callback so the animation isn't blocked by recalculating trace heights.
 */
const debouncedSlider = debounce((traceData: Model, row: SequenceAlignmentRowView) => {
  traceData.set('maxY', -1 * $(row).slider('value'));
}, 20);

class SequenceAlignmentRowView
  extends PolymerAlignmentRowView<AlignmentMapState, AlignmentTabState>
  implements MinimalSliceView
{
  alignment!: Alignment;
  mapstate!: AlignmentMapState;
  slice!: Slice;
  sliceView!: SliceView;
  tabView?: SequenceAlignmentTabView;

  constructor(options: PolymerAlignmentRowViewInitializationOptions<AlignmentMapState, AlignmentTabState>) {
    super(options);
  }

  events(): {[events: string]: string} {
    // eslint-disable-next-line no-restricted-syntax
    return _.extend(super.events(), {
      'click .times': 'showDeleteModal',
    });
  }

  // PolymerAlignmentRowView assumes all subclasses will return protein-style (uppercase) alignment types
  // TODO: should not return null
  getAlignmentRowType(): ProteinAlignmentKind | null {
    switch (this.alignment.get('type')) {
      case DNAAlignmentKind.CONSENSUS:
        return ALIGNMENT_TYPE.CONSENSUS;
      case DNAAlignmentKind.TEMPLATE:
        return ALIGNMENT_TYPE.TEMPLATE;
      default:
        return null;
    }
  }

  isValidCharacter(char: string): boolean {
    return util.isValidBase(char, this.alignment.get('sequence')!.baseTable()) && char !== GAP;
  }

  showInputBubble(options?: ShowInputBubbleOptions): void {
    // @ts-expect-error: bad class hierarchy
    SequenceMapUtil.showInputBubble<AlignmentMapState>(this, {...options, refocusTo: this.$el});
  }

  storeRegionInBenchlingClipboard(e: Event): string {
    const clipboardHash = SequenceMapUtil.maybeStoreNucleotideRegionInBenchlingClipboard(
      this.getSelection(),
      e
    );
    assertAndContinue(clipboardHash != null, 'Expected a clipboard hash');
    return clipboardHash!;
  }

  handlePasteText(text: string): void {
    SequenceMapUtil.handlePasteText(this.mapstate.get('sequence'), text);
  }

  handleCtrlShiftC(e: Event): void {
    SequenceMapUtil.handleCtrlShiftC(this.mapstate.get('sequence'), e);
  }

  mimicSelection(): void {
    // Copies selection from another alignment
    const props = this.tabstate.get('selectionProps');
    const selection = this.model.get('sequence').get('selection');
    selection.set(props.pick('start', 'end', 'anchor', 'active'), {propChange: true});
  }

  propSelection(): void {
    // Propogate selection to other alignments
    const props = this.tabstate.get('selectionProps');
    const selection = this.model.get('sequence').get('selection');
    props.set(
      {
        ...selection.pick('start', 'end', 'anchor', 'active'),
        // This is only stored so it can be used when jumping back to the prior location after undo
        rowIndex: this.tabstate.get('mapstates').indexOf(this.mapstate),
      },
      {cid: this.model.cid}
    );
  }

  getSelection(): Selection {
    return this.model.get('sequence').get('selection');
  }

  getResidueSlice(start: number, end: number): string {
    return this.model.get('sequence').residueSlice(start, end);
  }

  makeSliceView(): SliceView {
    const sliceView = alignmentviewutil.makeSliceView(
      this.slice,
      this.model,
      this.tabstate,
      this.tabstate.get('range')!,
      foreignObjectRenderingEnabled()
    );
    sliceView.setElement(SliceView.createSVGElement());
    return sliceView;
  }

  getRange(): Range {
    return this.tabstate.get('range')!;
  }

  getSlice(): Slice {
    return this.slice;
  }

  undo(): void {
    if (this.tabView) {
      this.tabView.undo();
    }
  }

  initialize(
    options: PolymerAlignmentRowViewInitializationOptions<AlignmentMapState, AlignmentTabState>
  ): void {
    super.initialize(options);
    this.tabView = options.tabView;
    this.alignment = this.model.get('alignment');
    this.mapstate = this.model; // for PolymerMapTabView
    this.slice = new Slice();
    this.slice.set(this.tabstate.get('slice').attributes);
    this.listenTo(this.tabstate.get('slice'), 'change', (slice: Slice): void => {
      this.slice.set(slice.attributes);
    });

    // Handle selection synching.
    this.mimicSelection();
    this.listenTo(
      this.tabstate.get('selectionProps'),
      'change',
      (model: AlignmentSelectionProps, changeOpts: {cid: string}): void => {
        if (changeOpts.cid !== this.model.cid) {
          this.mimicSelection();
        }
      }
    );
    this.listenTo(
      this.model.get('sequence').get('selection'),
      'change',
      (model: Selection, changeOpts: {propChange?: boolean} = {}): void => {
        if (!changeOpts.propChange) {
          this.propSelection();
        }
      }
    );

    const rerenderVisibilityOptions = (): void => {
      // This is hacky, but we want to re-render the visibility options dropdown without re-rendering the
      // entire alignment row.
      const {options, featureCounts} = this.getRowContentProps();
      renderToDOM(
        <AlignmentRowVisibilityOptionsDropdown options={options} featureCounts={featureCounts} />,
        this.$el.find('#visibility-options-react-mount')[0]
      );
    };

    this.listenTo(this.model.get('sequence').annotations(), 'add remove reset', () => {
      rerenderVisibilityOptions();
    });
    this.listenTo(this.model.get('sequence').translations(), 'add remove reset', () => {
      rerenderVisibilityOptions();
    });
    this.listenTo(this.model.get('sequence').sequencePrimers(), 'add remove reset', () => {
      rerenderVisibilityOptions();
    });
    this.listenTo(this.model.get('sequence').annotations(), 'layerChange', () => {
      this.sliceView.updateComponent(MapsConstants.SliceComponentName.annotations);
    });
    this.listenTo(this.model.get('sequence').translations(), 'layerChange', () => {
      this.sliceView.updateComponent(MapsConstants.SliceComponentName.translations);
    });
  }

  height(): number {
    return this.slice.get('height')!;
  }

  deleteAlignmentRow(): void {
    this.alignment.destroy();
    this.remove();
  }

  showDeleteModal(): void {
    new popup.Confirm({
      title: 'Remove sequence?',
      msg: `<p>
  Are you sure you want to remove the sequence,
  <b>${_.escape(this.model.get('sequence').name())}</b>, from the template alignment?</p>
  <p><b>Note</b>: To re-include this sequence in the alignment, use the
  <button type='button' class='button button--small button--secondary' style='pointer-events:none;'>
    Realign
  </button> feature.
</p>`,
    })
      .choice('Remove Sequence', 'button button--small button--primary js-submit', () => {
        this.deleteAlignmentRow();
      })
      .choice('Cancel', 'button button--small button--tertiaryGray js-cancel')
      .show();
  }

  update(): void {
    this.sliceView.update();
    this.renderNameInfo();
  }

  handleSliderShow(): void {
    const $sliderContainer = this.$el.find('.slider-container');
    if (
      this.sliceView.components.trace &&
      (this.sliceView.components.trace as unknown as TraceView).shouldShow()
    ) {
      $sliderContainer.show();
    } else {
      $sliderContainer.hide();
    }
  }

  renderNameInfo(): void {
    const alignmentType =
      this.tabstate.get('analysis')!.get('alignmentType') === DNAAlignmentKind.CONSENSUS
        ? ProteinAlignmentKind.CONSENSUS
        : ProteinAlignmentKind.TEMPLATE;
    const templateRow = this.tabstate
      .get('analysis')!
      .get('alignments')!
      .find((a: Alignment) => a.get('type') === DNAAlignmentKind.TEMPLATE);
    renderToDOM(
      <AlignmentRowInfo
        residues={this.model.get('alignment').get('sequence')!.get('bases')}
        trimStart={this.model.get('alignment').get('trimStart')}
        trimEnd={this.model.get('alignment').get('trimEnd')}
        visibilityOptions={this.model.get('visibilityOptions')}
        name={this.model.get('sequence').get('_sequence').get('name')}
        canEdit={this.model.get('permissions').polymerFeaturesEditable!}
        isProteinAlignment={false}
        alignmentType={alignmentType}
        nucleotideType={this.model.get('alignment').get('sequence')!.get('nucleotideType')}
        isReferenceRow={Boolean(this.getAlignmentRowType())}
        templateResidues={templateRow && templateRow.get('sequence')!.get('bases')}
        onNameChange={this.handleSequenceNameChange.bind(this)}
        reversed={Boolean(this.model.get('alignment').get('reversed'))}
      />,
      this.$el.find('.alignment-row-info-section')[0]
    );
  }

  getRowContentProps(): AlignmentRowProps {
    const canEdit = this.model.get('permissions').polymerFeaturesEditable!;
    return {
      alignmentRowType: this.alignment.get('type'),
      canEdit,
      reserveSpaceRight: canEdit || Boolean(this.alignment.get('trace')),
      options: this.visibilityOptions,
      featureCounts: {
        annotations: this.model.get('sequence').get('annotations').getCount(),
        translations: this.model.get('sequence').get('translations').getCount(),
        sequencePrimers: this.model.get('sequence').get('sequencePrimers').getCount(),
      },
    };
  }

  render(): this {
    super.render();

    this.renderNameInfo();

    const $handle = this.$el.find('.handle');
    const $sliderContainer = $handle.find('.slider-container');
    const traceData = this.alignment.get('trace');
    if (traceData != null) {
      // eslint-disable-next-line no-restricted-syntax
      const maxY = _.max(_.flatten(_.values(traceData.get('baseToColor'))));
      const sliderMin = -maxY;
      const $slider = $handle.find('.slider');
      $slider.slider({
        animate: 'fast',
        orientation: 'vertical',
        range: 'min',
        min: sliderMin,
        max: -25,
        value: sliderMin,
        slide(): void {
          debouncedSlider(traceData, this as SequenceAlignmentRowView);
        },
        change(): void {
          debouncedSlider(traceData, this as SequenceAlignmentRowView);
        },
      });
      traceData.set('maxY', maxY);
      this.handleSliderShow();
    } else {
      $sliderContainer.empty();
    }
    return this;
  }

  handleSequenceNameChange(name: string): void {
    this.model.get('sequence').get('_sequence').set({name});
    this.tabstate
      .get('analysis')!
      // @ts-expect-error: this save call goes through our backbone-extensions override first and eventually
      // runs the original Backbone.sync method which passes `pickSequenceFields` to `toJSON` which has is
      // overriden in AlignmentAnalysis.toJSON to handle the option.
      .save(null, {parse: false, patch: true, pickSequenceFields: ['name']})
      .done(alignmentviewutil.notifySuccess)
      .fail(alignmentviewutil.notifyError);
  }

  registerListeners(): void {
    super.registerListeners();
    this.listenTo(this.model, 'traceToggle', this.handleSliderShow.bind(this));
    this.listenTo(this.alignment, 'updateRow', this.update.bind(this));
    this.listenTo(this.model.get('sequence').get('_sequence'), 'change:name', this.renderNameInfo.bind(this));
    this.listenTo(
      this.model.get('visibilityOptions').getOption(vo.ALIGNMENT_ROW_STATS.name),
      'change:visible',
      this.renderNameInfo.bind(this)
    );
    this.listenTo(
      this.model.get('alignment'),
      'change:trimStart change:trimEnd',
      this.renderNameInfo.bind(this)
    );
    this.listenTo(
      this.tabstate.get('analysis')!.getMasterSequence(),
      'change:bases',
      this.renderNameInfo.bind(this)
    );
  }

  _contextMenuChoicesOptions(): MenuChoicesOptions {
    const polymer = this.model.get('sequence');
    return {
      alignment: true,
      circular: polymer.circular(),
      // Because the inheritance is a little messy, we need to smooth over the typescript errors here.
      // @ts-expect-error
      polymerMapTabView: this,
      hasTrim: polymer.get('trim') != null,
      tabstate: this.tabstate,
    };
  }
}
SequenceAlignmentRowView.initClass();

class AlignmentItemSource extends infinitescroller.ItemSource<SequenceAlignmentRowView> {
  model!: AlignmentTabState;
  tabView!: SequenceAlignmentTabView;
  indexOfFixed!: number;
  // TODO: Implement updateItem so we can reuse sliceviews

  initialize(tabstate: AlignmentTabState, tabView: SequenceAlignmentTabView): void {
    this.model = tabstate;
    this.tabView = tabView;
    const mapstates = tabstate.get('mapstates');
    const analysis = tabstate.get('analysis')!;
    for (let i = 0; i < mapstates.models.length; i++) {
      const ms = mapstates.models[i];
      if (ms.get('alignment').get('type') === analysis.get('alignmentType')) {
        this.indexOfFixed = i;
        break;
      }
    }
    assertAndContinue(this.indexOfFixed != null, 'Did not find fixed alignment row');
    this.updateLength();
    this.listenTo(mapstates, 'add remove reset', this.updateLength.bind(this));
  }

  updateLength(): void {
    this.set('length', this.model.get('mapstates').length - 1);
  }

  getItem(index: number): SequenceAlignmentRowView {
    const mapstates = this.model.get('mapstates');
    if (index >= this.indexOfFixed) {
      index++;
    }
    const mapstate = mapstates.models[index];
    const item = new SequenceAlignmentRowView({model: mapstate, tabstate: this.model, tabView: this.tabView});
    item.$el.attr('data-id', mapstate.get('alignment').cid);
    // TODO: fix & remove ignore
    // @ts-expect-error
    item.$el.attr('data-view', item);
    setTestAttrs(item.$el, {component: 'AlignmentRow', element: 'root'});
    return item;
  }

  estimatedHeight(): number {
    return 30;
  }
}

interface AlignmentRowsViewInitOptions {
  model: AlignmentTabState;
  tabView: SequenceAlignmentTabView;
  saveOrderHandler: (cids: Array<string>) => void;
}

class AlignmentRowsView extends Backbone.View<AlignmentTabState> implements RowsView {
  saveOrderHandler!: (cids: Array<string>) => void;
  views!: Array<SequenceAlignmentRowView>;
  tabView?: SequenceAlignmentTabView;

  static initClass(): void {
    this.prototype.className = 'scrolling-alignment-rows';
  }

  constructor(options: AlignmentRowsViewInitOptions) {
    super(options);
  }

  initialize(options: AlignmentRowsViewInitOptions): void {
    this.saveOrderHandler = options.saveOrderHandler;
    this.views = [];
    this.tabView = options.tabView;
  }

  die(): void {
    for (let i = 0; i < this.views.length; i++) {
      const view = this.views[i];
      view.die();
    }
    // see https://jqueryui.com/sortable/
    this.$el.find('.sortable-wrapper').sortable('destroy');
  }

  update(): void {
    const $wrapper = this.$el.find('.sortable-wrapper');
    $wrapper.empty();

    // Remove all rendered views
    for (const view of this.views) {
      view.remove();
    }
    this.views = [];
    // Re-render them.
    for (const alignmentMapState of this.model.get('mapstates').models) {
      if (
        alignmentMapState.get('alignment').get('type') !== this.model.get('analysis')!.get('alignmentType')
      ) {
        const view = new SequenceAlignmentRowView({
          model: alignmentMapState,
          tabstate: this.model,
          tabView: this.tabView,
        });
        const {el} = view.render();
        $(el).attr('data-id', alignmentMapState.get('alignment').cid);
        $(el).data('data-view', view);
        addTestAttrsToDOMElement(el, {component: 'AlignmentRow', element: 'root'});
        $wrapper.append(el);
        this.views.push(view);
      }
    }
  }

  showRange(left: number | null = null): void {
    for (let i = 0; i < this.views.length; i++) {
      const view = this.views[i];
      view.showRange(left);
    }
  }

  resizeSVGs(): void {
    for (let i = 0; i < this.views.length; i++) {
      const view = this.views[i];
      view.setSVGWidth();
    }
  }

  _saveOrder(): void {
    const cids = this.$el
      .find('.alignment-row')
      .toArray()
      .map((row) => $(row).attr('data-id'));
    this.saveOrderHandler(cids);
  }

  handleAlignmentSort(): void {
    // Map each slice to an index.
    const cidOrder: {[cid: string]: number} = {};
    this.model
      .get('analysis')!
      .get('alignments')!
      .models.forEach((model: Alignment, i: number) => {
        cidOrder[model.cid] = i;
      });
    // Leave the top view alone.
    // eslint-disable-next-line no-restricted-syntax
    const alignedViews = _.filter(this.views, (view) => cidOrder[view.$el.data('id')] > 0);
    const $viewEls = $(alignedViews.map((view) => view.el));
    const $parent = $viewEls.parent();
    // Sort slices by index (ignore JQuery typing)
    // @ts-expect-error
    $viewEls.detach().sort((nodeA, nodeB) => {
      const nodeAOrder = cidOrder[$(nodeA).data('id')];
      const nodeBOrder = cidOrder[$(nodeB).data('id')];
      return nodeAOrder - nodeBOrder;
    });
    $parent.append($viewEls);
  }

  render(): AlignmentRowsView {
    // Make alignment views sortable.
    const $wrapper = this.$el.find('.sortable-wrapper');
    // see https://jqueryui.com/sortable/
    $wrapper.sortable({
      items: '> .alignment-row',
      handle: '.handle',
      // @ts-expect-error: unexpected and possibly unused argument
      cursorOffset: `url(${staticURL('/static/img/closedhand.cur')}), pointer`,
      forcePlaceholderSize: true,
      // TODO fill in
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      start(e: any, ui: any): number {
        return ui.placeholder.height(ui.item.height());
      },
      scroll: false,
      axis: 'y',
      stop: () => {
        this._saveOrder();
      },
    });
    $wrapper.mousedown(() => {
      // assume document is not null
      // @ts-expect-error
      document.activeElement.blur();
    });

    $wrapper.on('sortstop', (e, ui) => {
      const dataView = $(ui.item).data('data-view');
      if (dataView) {
        dataView.showRange();
      }
    });
    this.update();
    return this;
  }

  jumpToIndex(index: number): void {
    const mapStateEl = this.views[index].$el[0];
    // Don't use native `scrollIntoView` even with {behavior: smooth} - it doesn't animate as nicely
    verticallyScrollItemIntoView(this.el, mapStateEl);
  }
}
AlignmentRowsView.initClass();

interface InfiniteAlignmentRowsViewInitOptions {
  model: AlignmentTabState;
  tabView: SequenceAlignmentTabView;
}
class InfiniteAlignmentRowsView extends Backbone.View<AlignmentTabState> implements RowsView {
  hasAttached?: boolean;
  infiniteScroller!: InfiniteScroller<SequenceAlignmentRowView>;
  infiniteScrollbar!: InfiniteScrollbar;
  itemSource!: AlignmentItemSource;
  scrollData!: ScrollData;

  static initClass(): void {
    this.prototype.className = 'infinite-alignment-rows';
  }

  constructor(options: InfiniteAlignmentRowsViewInitOptions) {
    super(options);
  }

  initialize(options: InfiniteAlignmentRowsViewInitOptions): void {
    this.scrollData = new infinitescroller.ScrollData();
    this.itemSource = new AlignmentItemSource(this.model, options.tabView);
    this.infiniteScroller = new infinitescroller.InfiniteScroller({
      tabstate: this.model,
      itemSource: this.itemSource,
      scrollData: this.scrollData,
      spacing: 0,
    });
    this.infiniteScrollbar = new infinitescroller.InfiniteScrollbar({
      tabstate: this.model,
      model: this.scrollData,
      fullHeight: () => this.$el.height(),
    });
    this.listenTo(this.itemSource, 'change:length', this.rerenderRows.bind(this));
  }

  die(): void {
    // TODO
  }

  rerenderRows(): void {
    const topIndex = this.infiniteScroller.firstRendered()!.index();
    this.infiniteScroller.bootstrap({index: topIndex, flush: true, topAnchor: 10000});
    this.infiniteScroller.jumpToIndex(topIndex, 0);
  }

  resizeSVGs(): void {
    for (let i = 0; i < this.infiniteScroller.renderedItems.length; i++) {
      const item = this.infiniteScroller.renderedItems[i];
      const view = item.item();
      view.setSVGWidth();
    }
  }

  showRange(left: number | null = null): void {
    for (let i = 0; i < this.infiniteScroller.renderedItems.length; i++) {
      const item = this.infiniteScroller.renderedItems[i];
      const view = item.item();
      view.showRange(left);
    }
  }

  handleAlignmentSort(): void {
    this.infiniteScroller.bootstrap({index: 0, flush: true, topAnchor: 0});
    this.infiniteScroller.jumpToIndex(0, 0);
  }

  update(): void {
    if (!this.hasAttached) {
      const $scrollWrapper = $('<div class="reset scroll-wrapper"></div>');
      this.$el.append($scrollWrapper);
      $scrollWrapper.append(this.infiniteScroller.render().el);
      $scrollWrapper.append(this.infiniteScrollbar.render().el);
      this.hasAttached = true;
    }
  }

  render(): InfiniteAlignmentRowsView {
    this.update();
    return this;
  }

  jumpToIndex(index: number): void {
    this.infiniteScroller.jumpToIndex(index);
  }
}
InfiniteAlignmentRowsView.initClass();

export {SequenceAlignmentRowView, InfiniteAlignmentRowsView, AlignmentRowsView};
